// OverDMA: "Notify me when available" on sold-out products (snippets/odma-notify-dialog.liquid).
// A [data-notify-me] button opens the window for its product; on a product page or in quick add it
// also names the option selected there. Sending is Shopify's newsletter form (a normal page post): the
// shopper is saved in Customers, subscribed to email marketing and tagged with the product. Back on
// the page (?customer_posted=true#OdmaNotifyForm) the page returns to the button the shopper pressed
// and the window opens again with the thanks, or with the error and the same product.
(() => {
  const dialog = document.getElementById('OdmaNotifyDialog');
  if (!dialog || typeof dialog.showModal !== 'function') return;
  const form = dialog.querySelector('form');
  const send = form.querySelector('[type="submit"]');
  const views = {
    done: dialog.querySelector('[data-notify-view="done"]'),
    form: dialog.querySelector('[data-notify-view="form"]'),
  };
  const KEY = 'odma-notify-request';

  const show = (name) => {
    views.done.hidden = name !== 'done';
    views.form.hidden = name !== 'form';
  };
  const fill = (request) => {
    dialog.querySelector('[data-notify-field="tags"]').value = request.tags;
    dialog.querySelector('[data-notify-product]').textContent = request.product;
  };
  // a customer tag cannot hold a comma (it separates tags)
  const tag = (text) => text.replace(/,/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120);

  // The page behind, the window's origin and the busy button work as for the terms window
  // (assets/product-form.js: pinPage, anchor, busy).
  const pinPage = () => {
    const body = document.body;
    if (!window.matchMedia('(pointer: coarse)').matches || getComputedStyle(body).position === 'fixed') return () => {};
    const y = window.scrollY;
    Object.assign(body.style, { position: 'fixed', top: `${-y}px`, left: '0', right: '0', width: '100%' });
    return () => {
      Object.assign(body.style, { position: '', top: '', left: '', right: '', width: '' });
      const html = document.documentElement;
      const behavior = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      // a programmatic jump (contract C1): the sticky header must not read it as a scroll down
      html.dataset.odmaJump = '1';
      window.scrollTo(0, y);
      html.style.scrollBehavior = behavior;
      requestAnimationFrame(() => requestAnimationFrame(() => delete html.dataset.odmaJump));
    };
  };
  const anchor = (trigger) => {
    const box = trigger && trigger.getBoundingClientRect();
    if (!box || !box.width || window.matchMedia('(max-width: 749px)').matches) {
      dialog.style.removeProperty('transform-origin');
      return;
    }
    const view = document.documentElement;
    const x = Math.min(Math.max(box.left + box.width / 2, 0), view.clientWidth) - (view.clientWidth - dialog.offsetWidth) / 2;
    const y = Math.min(Math.max(box.top + box.height / 2, 0), view.clientHeight) - (view.clientHeight - dialog.offsetHeight) / 2;
    dialog.style.transformOrigin = `${Math.round(x)}px ${Math.round(y)}px`;
  };
  const busy = (button, on) => {
    button.classList.toggle('loading', on);
    if (on) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !on);
  };

  let openedAt = 0;
  let unpin = null; // puts back the page this window pinned
  const releasePage = () => {
    if (unpin) unpin();
    unpin = null;
  };
  const open = (trigger) => {
    if (dialog.open) return;
    dialog.showModal();
    unpin = pinPage();
    anchor(trigger);
    openedAt = performance.now();
  };

  // the product the button is for, with the option selected on its product page or quick add, and the
  // tags it gives the shopper: notify-me, notify-<handle> and notify-<handle>: <option>
  const describe = (button) => {
    let product = button.dataset.product || document.title;
    const link = new URL(button.dataset.url || location.pathname, location.origin);
    let handle = (link.pathname.match(/\/products\/([^/]+)/) || [])[1] || '';
    try {
      handle = decodeURIComponent(handle);
    } catch (e) {
      // keep it as it is
    }
    const tags = ['notify-me'];
    if (handle) tags.push(tag(`notify-${handle}`));
    const selected = button.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');
    if (selected) {
      try {
        const variant = JSON.parse(selected.textContent);
        if (variant && variant.title && variant.title !== 'Default Title') {
          product += ` — ${variant.title}`;
          if (handle) tags.push(tag(`notify-${handle}: ${variant.title}`));
        }
      } catch (e) {
        // no option to name: the product alone
      }
    }
    return { product, tags: tags.join(', ') };
  };

  // where the button was: Shopify's post comes back as a new page, which would start at the top
  const placeOf = (button) => {
    const body = document.body;
    // a layer behind (quick add) may have pinned the page: its offset is then the scroll position
    const pinned = getComputedStyle(body).position === 'fixed' ? -parseFloat(body.style.top) || 0 : 0;
    return {
      path: location.pathname,
      url: button.dataset.url || '',
      section: (button.closest('.shopify-section') || {}).id || '',
      y: Math.round(window.scrollY + pinned),
    };
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-notify-me]');
    if (!button) return;
    event.preventDefault();
    // a request is already open (or being sent): it must keep its own product and tags
    if (dialog.open) return;
    const request = describe(button);
    fill(request);
    try {
      sessionStorage.setItem(KEY, JSON.stringify({ ...request, place: placeOf(button) }));
    } catch (e) {
      // without storage the request is still sent; only a failed send would lose the product
    }
    dialog.querySelector('[data-notify-error]')?.remove();
    show('form');
    open(button);
  });

  dialog.querySelectorAll('[data-notify-close]').forEach((button) => button.addEventListener('click', () => dialog.close()));

  // Shopify protects contact forms with hCaptcha. When it asks for a challenge, the challenge is added
  // to the page, and a modal window stays on top of the page and makes it inert, so the shopper could
  // not answer it. While the form is sent, the window stops being modal and stays where it is
  // (.is-sending in assets/odma-theme.css). "Send" shows the post going on and a second press posts
  // nothing; it can be pressed again after 8 s if no page came (a challenge left unanswered).
  let sending = false;
  let sendTimer;
  const setSending = (on) => {
    sending = on;
    busy(send, on);
    clearTimeout(sendTimer);
    if (on) sendTimer = setTimeout(() => setSending(false), 8000);
  };
  // captured on the window, before any listener on the form (Shopify's would post again)
  dialog.addEventListener(
    'submit',
    (event) => {
      if (!sending) return;
      event.preventDefault();
      event.stopPropagation();
    },
    true
  );
  form.addEventListener('submit', () => {
    if (!dialog.open) return;
    if (!dialog.classList.contains('is-sending')) {
      // the page is given back too: under the pinned body, a challenge placed at the scroll position
      // would land off screen
      releasePage();
      dialog.close();
      dialog.show();
      dialog.classList.add('is-sending');
      // the page under the dimmed area takes no taps while the post waits (assets/odma-theme.css)
      document.documentElement.classList.add('odma-notify-sending');
      // show() focuses the email field: on phones that would bring the keyboard back over the challenge
      send.focus({ preventScroll: true });
    }
    setSending(true);
  });
  dialog.addEventListener('close', () => {
    // the close() above also fires this, after show(): the window is open again then
    if (dialog.open) return;
    dialog.classList.remove('is-sending');
    document.documentElement.classList.remove('odma-notify-sending');
    setSending(false);
    releasePage();
  });
  // back on this page from the browser's cache after sending: the window is not left half sent
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && dialog.open) dialog.close();
  });

  // a click on the backdrop closes it too (not one on its scrollbar, nor the end of a text selection,
  // nor the second tap of a double tap on "Notify me")
  const outside = (event) => {
    const rect = dialog.getBoundingClientRect();
    return (
      event.target === dialog &&
      (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)
    );
  };
  let pressedOutside = false;
  dialog.addEventListener('pointerdown', (event) => {
    pressedOutside = outside(event) && performance.now() - openedAt > 400;
  });
  dialog.addEventListener('click', (event) => {
    if (pressedOutside && outside(event)) dialog.close();
  });
  // Esc closes it on keydown; the keyup would then reach the quick-add window behind it and close that too
  dialog.addEventListener('cancel', () => {
    const stop = (event) => {
      if (event.key === 'Escape') event.stopPropagation();
    };
    window.addEventListener('keyup', stop, { capture: true, once: true });
    setTimeout(() => window.removeEventListener('keyup', stop, { capture: true }), 1000);
  });

  // assets/odma-catalog.js puts the shopper's tab and filters back on this load, then says so
  // (odma:catalog-ready, data-odma-catalog-ready); a page without a catalog has nothing to wait for
  const catalogsReady = () =>
    new Promise((resolve) => {
      if (
        document.documentElement.dataset.odmaCatalogReady ||
        !document.querySelector('[data-odma-games], [data-odma-hardware], [data-odma-game]')
      ) {
        resolve();
        return;
      }
      const timer = setTimeout(resolve, 1500);
      document.addEventListener(
        'odma:catalog-ready',
        () => {
          clearTimeout(timer);
          resolve();
        },
        { once: true }
      );
    });
  // brings the page back to the button the shopper pressed (instant, centred) or, when it is not shown
  // now, to where they were; it takes the focus, so closing the window lands on it too
  const goBack = (place) => {
    const scope = document.getElementById(place.section) || document;
    const trigger = [...scope.querySelectorAll('[data-notify-me], [data-product-url]')].find(
      (button) => (button.dataset.url || button.dataset.productUrl) === place.url && button.getClientRects().length > 0
    );
    const root = document.documentElement.style;
    const behavior = root.scrollBehavior;
    root.scrollBehavior = 'auto';
    if (trigger) trigger.scrollIntoView({ block: 'center' });
    else window.scrollTo(0, Number(place.y) || 0);
    root.scrollBehavior = behavior;
    if (trigger) trigger.focus({ preventScroll: true });
    return trigger;
  };

  // back from sending
  if (location.hash === `#${form.id}`) {
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(KEY));
    } catch (e) {
      // the shopper can open it again from the button
    }
    const failed = dialog.querySelector('[data-notify-error]');
    if (failed && saved) fill(saved);
    // a reload must not open it again
    const url = new URL(location.href);
    url.searchParams.delete('customer_posted');
    url.hash = '';
    history.replaceState(history.state, '', url);
    const place = saved && saved.place && saved.place.path === location.pathname ? saved.place : null;
    const reopen = () => {
      const trigger = place ? goBack(place) : null;
      if (failed || !views.done.hidden) open(trigger);
    };
    // with a place to go back to, the catalogs first put back the shopper's tab and filters
    if (place) catalogsReady().then(reopen);
    else reopen();
  }
})();
